// src/PlatForms/SelfLMS/Hooks/Courses/usePageDetails.js
import { useState, useEffect } from 'react';
import { selfLearnerService } from 'PlatForms/SelfLMS/Services/Courses/CourseService';

const usePageDetails = (moduleId, pageId, moduleProgressId, isAssessment = false) => {
  const [pageData, setPageData] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPageData = async () => {
    try {
      console.log(' Fetching page data for module:', moduleId, 'page:', pageId, 'isAssessment:', isAssessment);
      setLoading(true);
      setError(null);
      
      // Pass isAssessment parameter to the service
      const data = await selfLearnerService.getLearnerPageData(moduleId, pageId, isAssessment);
      console.log(' Page data received:', data);
      console.log('📄 Current page file ID:', data.currentPage?.pageFileId);
      setPageData(data);
      
      // Load page file only if it's NOT an assessment page and pageFileId exists
      if (!isAssessment && data.currentPage?.pageFileId) {
        console.log('📁 Loading file with pageFileId:', data.currentPage.pageFileId);
        await loadPageFile(data.currentPage.pageFileId);
      } else if (isAssessment) {
        console.log(' Assessment page - skipping file load');
        setFileContent(null);
        setFileType(null);
      } else {
        console.log('⚠️ No pageFileId found for this page');
        setFileContent(null);
        setFileType(null);
      }
      
    } catch (err) {
      console.error(' Error fetching page data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load page data');
      setPageData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPageFile = async (pageFileId) => {
    try {
      console.log(' Loading page file for ID:', pageFileId);
      
      if (!pageFileId) {
        console.log('⚠️ No pageFileId provided');
        setFileContent(null);
        setFileType(null);
        return;
      }
      
      const fileBlob = await selfLearnerService.getLearnerPageFile(pageFileId);
      
      // Detect file type from blob
      const fileUrl = URL.createObjectURL(fileBlob);
      const detectedFileType = detectFileType(fileBlob);
      
      setFileContent(fileUrl);
      setFileType(detectedFileType);
      
      console.log(' Page file loaded, type:', detectedFileType);
      
    } catch (err) {
      console.error(' Error loading page file:', err);
      setFileContent(null);
      setFileType(null);
    }
  };

  const detectFileType = (blob) => {
    const type = blob.type;
    
    if (type.includes('pdf')) return 'pdf';
    if (type.includes('image')) return 'image';
    if (type.includes('text') || type.includes('html')) return 'text';
    if (type.includes('word') || type.includes('document')) return 'document';
    if (type.includes('excel') || type.includes('sheet')) return 'spreadsheet';
    if (type.includes('zip') || type.includes('compressed')) return 'archive';
    
    return 'unknown';
  };

  const markPageComplete = async () => {
    try {
      if (!moduleProgressId || !pageId) {
        throw new Error('Missing moduleProgressId or pageId');
      }
      
      await selfLearnerService.markPageAsCompleted(moduleProgressId, pageId);
      console.log(' Page marked as completed');
      return true;
    } catch (err) {
      console.error(' Error marking page as completed:', err);
      throw err;
    }
  };

  useEffect(() => {
    if (moduleId) {
      fetchPageData();
    }
  }, [moduleId, pageId, isAssessment]); // Add isAssessment to dependencies

  const refetch = () => {
    fetchPageData();
  };

  return {
    pageData,
    fileContent,
    fileType,
    loading,
    error,
    markPageComplete,
    refetch
  };
};

export default usePageDetails;